/*
 * Death By Stickers designer - the store side (runs on the Shopify product page).
 *
 * The designer runs on our own server inside an <iframe data-dbs-designer>. This script is
 * the only thing that talks to Shopify: when the designer says "add this design", it adds
 * the product to the cart with the design's details (line-item properties). Messages are
 * only accepted from the designer's own address, and only this product's variants can be
 * added, so nothing else can use it to change the cart.
 *
 * Messages (designer -> store):
 *   dbs:ready                    -> reply dbs:cart {count, max, start}  (start: an Edit design / Reorder link)
 *   dbs:height {h}               -> size the iframe to the designer's content
 *   dbs:modal {open}             -> while a pop-up (editor, crop...) is open, the iframe fills the screen
 *   dbs:add {id, variantId, quantity, properties, replaceKey} -> /cart/add.js (then removes the
 *                                  replaced line, for "Edit design"), reply dbs:added {id, ok, count, error}
 *   dbs:go-cart                  -> go to /cart
 *   dbs:scroll-top               -> scroll the designer into view (after "Design another")
 *
 * iframe attributes: data-dbs-designer, data-product-id, data-variants='[id,...]', data-max="5"
 *
 * Page links: ?edit=<designId>&line=<cart line key> reopens a cart line's design ("Edit design" in the
 * cart); ?reorder=<designId> reopens a past order's design (the order email's "Reorder" link).
 * If the designer doesn't answer within 15 s, a friendly "isn't loading" note is shown.
 */
(() => {
  function setup(frame) {
    const app = new URL(frame.src, location.href).origin;
    const productId = Number(frame.dataset.productId);
    const productIds = JSON.parse(frame.dataset.productIds || "[]").map(Number).concat(productId);
    const variants = JSON.parse(frame.dataset.variants || "[]").map(Number);
    const max = Number(frame.dataset.max || 0);          // 0 = no limit
    let modal = false, lastHeight = 0, savedScroll = 0, heard = false;

    // Edit design / Reorder links (only well-formed values are passed on).
    const q = new URLSearchParams(location.search), ID = /^[A-Za-z0-9_-]{24}$/, KEY = /^[0-9]+:[0-9a-f]{8,64}$/;
    const start = ID.test(q.get("edit") || "") ? { edit: q.get("edit"), line: KEY.test(q.get("line") || "") ? q.get("line") : null }
                : ID.test(q.get("reorder") || "") ? { reorder: q.get("reorder") } : null;

    // If the designer server doesn't answer, say so instead of showing an empty box.
    const note = document.createElement("div");
    note.hidden = true;
    note.style.cssText = "padding:18px;border:1px solid #555;margin:12px 0;text-align:center;";
    note.innerHTML = "Our sticker designer isn't loading right now. Please refresh the page in a minute - " +
                     "if it still won't load, <a href=\"/pages/contact\">contact us</a> and we'll get your order sorted.";
    frame.insertAdjacentElement("afterend", note);
    setTimeout(() => { if (!heard) note.hidden = false; }, 15000);

    const send = (msg) => frame.contentWindow && frame.contentWindow.postMessage(msg, app);

    // How many lines of THIS product are already in the cart (each design is its own line).
    async function count() {
      const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then(r => r.json());
      return cart.items.filter(i => productIds.includes(i.product_id)).length;
    }

    function setModal(open) {
      if (open === modal) return;
      modal = open;
      if (open) {
        savedScroll = window.scrollY;
        Object.assign(frame.style, { position: "fixed", inset: "0", width: "100%", height: "100%", zIndex: "2147483000" });
        document.documentElement.style.overflow = "hidden";
      } else {
        Object.assign(frame.style, { position: "", inset: "", width: "100%", height: lastHeight + "px", zIndex: "" });
        document.documentElement.style.overflow = "";
        window.scrollTo(0, savedScroll);
      }
    }

    // Line-item properties: short plain text only.
    function cleanProps(p) {
      const out = {};
      Object.entries(p || {}).slice(0, 20).forEach(([k, v]) => {
        if (typeof k === "string" && (typeof v === "string" || typeof v === "number")) out[k.slice(0, 60)] = String(v).slice(0, 1000);
      });
      return out;
    }

    async function add(m) {
      try {
        const variant = variants.includes(Number(m.variantId)) ? Number(m.variantId) : variants[0];
        if (!variant) throw new Error("This product can't be added right now");
        if (max && !m.replaceKey && (await count()) >= max) throw new Error(`You can add up to ${max} designs.`);
        const r = await fetch("/cart/add.js", {
          method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: variant, quantity: Math.max(1, Number(m.quantity) || 1), properties: cleanProps(m.properties) }] }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.description || data.message || "Couldn't add it to your cart");
        if (m.replaceKey && /^[0-9]+:[0-9a-f]{8,64}$/.test(m.replaceKey)) {      // Edit design: drop the old line
          await fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
                                           body: JSON.stringify({ id: m.replaceKey, quantity: 0 }) }).catch(() => null);
        }
        const n = await count();
        send({ type: "dbs:added", id: m.id, ok: true, count: n, max });
        document.dispatchEvent(new CustomEvent("dbs:cart-updated", { detail: { count: n } }));
      } catch (e) {
        send({ type: "dbs:added", id: m.id, ok: false, error: e.message });
      }
    }

    window.addEventListener("message", (e) => {
      if (e.origin !== app || e.source !== frame.contentWindow) return;
      const m = e.data || {};
      heard = true; note.hidden = true;
      if (m.type === "dbs:ready") count().then(n => send({ type: "dbs:cart", count: n, max, start })).catch(() => send({ type: "dbs:cart", count: 0, max, start }));
      else if (m.type === "dbs:height") { lastHeight = Math.max(300, Math.min(20000, Number(m.h) || 0)); if (!modal) frame.style.height = lastHeight + "px"; }
      else if (m.type === "dbs:modal") setModal(!!m.open);
      else if (m.type === "dbs:add") add(m);
      else if (m.type === "dbs:go-cart") location.href = "/cart";
      else if (m.type === "dbs:scroll-top") frame.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
  const start = () => document.querySelectorAll("iframe[data-dbs-designer]").forEach(setup);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start); else start();
})();
